/**
 * 指导老师列表页（F3 / T77）：跨校认证导师池。
 *
 * - 卡片：姓名 / 学校 / 职称 / 研究方向标签 / 实带队伍数（实时派生）；
 * - 研究方向 chips 过滤；
 * - 队长可把自己带队的那支队伍绑定给某位导师（余量 ≤ 0 的后端 409，前端按钮禁用）；
 *   已绑定的队伍可一键解绑。多队时页顶先选目标队伍。
 * 演示数据：到「设置与合规 → 演示模式」一键载入，会带出 6 位松江七校导师。
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { advisorsApi } from '../api/advisors.js';
import type { AdvisorDto, TeamDto } from '../api/types.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { Loading } from '../components/Loading.js';
import { Tag } from '../components/Tag.js';
import { useAdvisorsQuery, useMyTeamsQuery } from '../hooks/queries.js';
import { qk } from '../lib/queryKeys.js';
import { schoolFullLabel } from '../lib/format.js';
import { useSession } from '../store/session.js';
import { toast } from '../store/toast.js';

/** 我作为队长的队伍（可绑定导师的对象）。 */
function myLeaderTeams(teams: TeamDto[] | undefined, userId: string | undefined): TeamDto[] {
  if (teams === undefined || userId === undefined) return [];
  return teams.filter((team) => team.ownerId === userId);
}

export function AdvisorsPage() {
  const user = useSession((state) => state.user);
  const advisorsQuery = useAdvisorsQuery();
  const myTeamsQuery = useMyTeamsQuery();
  const queryClient = useQueryClient();

  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const eligibleTeams = useMemo(
    () => myLeaderTeams(myTeamsQuery.data?.teams, user?.id),
    [myTeamsQuery.data?.teams, user?.id],
  );
  const [teamId, setTeamId] = useState<string>('');
  const selectedTeam = eligibleTeams.find((team) => team.id === teamId) ?? eligibleTeams[0];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: qk.advisors() });
    void queryClient.invalidateQueries({ queryKey: qk.myTeams() });
  };

  const bind = useMutation({
    mutationFn: (advisorUserId: string) => {
      if (selectedTeam === undefined) throw new Error('请先创建或选择一支队伍');
      return advisorsApi.bind(selectedTeam.id, advisorUserId);
    },
    onSuccess: (_data, advisorUserId) => {
      const advisor = advisorsQuery.data?.advisors.find((item) => item.userId === advisorUserId);
      invalidate();
      toast.success('已邀请导师指导', advisor ? `${advisor.name} 已绑定到「${selectedTeam?.name}」` : undefined);
    },
  });

  const unbind = useMutation({
    mutationFn: () => {
      if (selectedTeam === undefined) throw new Error('请先选择队伍');
      return advisorsApi.unbind(selectedTeam.id);
    },
    onSuccess: () => {
      invalidate();
      toast.success('已解除导师绑定', selectedTeam ? `「${selectedTeam.name}」现在没有指导老师` : undefined);
    },
  });

  if (advisorsQuery.isPending || myTeamsQuery.isPending) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">指导老师</h1>
        </div>
        <Loading label="正在读取导师列表…" rows={3} />
      </div>
    );
  }

  if (advisorsQuery.isError) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-title">指导老师</h1>
        </div>
        <ErrorState error={advisorsQuery.error} title="读取导师列表失败" onRetry={() => void advisorsQuery.refetch()} />
      </div>
    );
  }

  const advisors = advisorsQuery.data.advisors;
  const allTags = Array.from(new Set(advisors.flatMap((advisor) => advisor.researchTags)));
  const filtered = tagFilter === null ? advisors : advisors.filter((advisor) => advisor.researchTags.includes(tagFilter));

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-title">指导老师</h1>
          <p className="page-sub">
            松江七校已认证导师池：按研究方向找老师，一键绑定到自己带队的队伍。名额实时计算，满员即锁定。
          </p>
        </div>
      </div>

      {eligibleTeams.length > 0 ? (
        <div className="row-tight advisor-team-picker">
          <span className="muted small">绑定到队伍：</span>
          {eligibleTeams.length === 1 ? (
            <strong>{selectedTeam?.name}</strong>
          ) : (
            <select
              className="input"
              value={selectedTeam?.id ?? ''}
              onChange={(event) => setTeamId(event.target.value)}
              aria-label="选择要绑定导师的队伍"
            >
              {eligibleTeams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                  {team.advisorId ? '（已有导师）' : ''}
                </option>
              ))}
            </select>
          )}
          {selectedTeam?.advisorId ? (
            <span className="small muted">
              已有导师：
              {advisors.find((advisor) => advisor.userId === selectedTeam.advisorId)?.name ?? selectedTeam.advisorId}
            </span>
          ) : null}
        </div>
      ) : (
        <p className="muted small">
          你还没有自己带队的队伍——先去<Link to="/team">「我的队伍」</Link>建一支，才能邀请导师指导。
        </p>
      )}

      {allTags.length > 0 ? (
        <div className="chip-grid" role="group" aria-label="按研究方向过滤">
          <button
            type="button"
            className={tagFilter === null ? 'chip on' : 'chip'}
            onClick={() => setTagFilter(null)}
          >
            全部
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              type="button"
              className={tagFilter === tag ? 'chip on' : 'chip'}
              onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      ) : null}

      {advisors.length === 0 ? (
        <EmptyState
          icon="◇"
          title="还没有认证导师"
          description={
            '导师池为空。到「设置与合规 → 演示模式」一键载入演示数据，即可看到 6 位松江七校导师的样例。'
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState icon="◇" title="该方向下暂无导师" description="换个研究方向标签看看，或清除过滤。" />
      ) : (
        <div className="grid-2">
          {filtered.map((advisor: AdvisorDto) => {
            const isBoundToSelected = selectedTeam?.advisorId === advisor.userId;
            const full = advisor.remaining <= 0;
            return (
              <div key={advisor.userId} className="card advisor-card">
                <div className="row-tight">
                  <strong>{advisor.name}</strong>
                  <Tag tone="muted">{schoolFullLabel(advisor.school)}</Tag>
                  {full ? <Tag tone="danger">名额已满</Tag> : <Tag tone="ok">余 {advisor.remaining} 席</Tag>}
                </div>
                <div className="muted small">{advisor.title}</div>
                <div className="row-tight">
                  {advisor.researchTags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </div>
                <div className="muted small">
                  已指导 {advisor.currentLoad} / {advisor.capacity} 支队伍
                </div>
                {selectedTeam !== undefined ? (
                  <div className="row-tight">
                    {isBoundToSelected ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={unbind.isPending}
                        onClick={() => unbind.mutate()}
                      >
                        解除绑定
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={bind.isPending || full}
                        title={full ? '该导师指导名额已满' : undefined}
                        onClick={() => bind.mutate(advisor.userId)}
                      >
                        邀请指导
                      </Button>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
